import { native, type SubscriptionProduct } from '@forest/native-services'
import {
  FailureCauseError,
  nativeErrorCause,
  tagFailure,
} from '@forest/paywall-core'
import { openLoadFailureDialog } from './load-failure-dialog'
import { language } from './params'
import { buildPaywallUrl } from './paywall-url'
import { isTrialEligible } from './trial-eligibility'

// Fetch the store's subscription products to decide trial eligibility, retrying
// once before giving up with the last failure.
async function fetchSubscriptionProducts(): Promise<SubscriptionProduct[]> {
  let failure: unknown
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const result = await native.iap.getAvailableSubscriptionProducts()
      if (result.success) return result.data.products
      failure = new FailureCauseError(
        nativeErrorCause(result.error),
        'Failed to get subscription products',
      )
    } catch (error) {
      failure = error
    }
  }
  throw tagFailure(failure, 'load_store_products')
}

// Route a non-subscriber to the Forest Plus paywall: trial-paywall while they
// are still trial-eligible, otherwise permanent-paywall. Navigates the same
// WebView with location.href so the paywall can return here via history.back
// (see ADR 0039). On a double fetch failure we surface the shared error dialog
// and do not navigate, mirroring the service-level claim path in page.tsx.
export async function openPlusPaywall(): Promise<void> {
  let products: SubscriptionProduct[]
  try {
    products = await fetchSubscriptionProducts()
  } catch (error) {
    openLoadFailureDialog(error, { closesPage: false })
    return
  }

  const target = isTrialEligible(products) ? 'trial' : 'permanent'
  native.general.logEvent({
    event_name: 'cta_to_paywall',
    event_params: { source: 'daily_focus_challenge' },
  })
  location.href = buildPaywallUrl({
    target,
    mode: import.meta.env.MODE,
    language,
    search: location.search,
  })
}
